import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { ScoreDisplay } from '../components/ScoreDisplay';
import { MetricCard } from '../components/MetricCard';
import { AlertBadge } from '../components/AlertBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { 
  Loader2, 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  User, 
  RefreshCw,
  Trophy,
  AlertTriangle
} from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScorecardView() {
  const { entryId } = useParams();
  const [loading, setLoading] = useState(true);
  const [scorecard, setScorecard] = useState(null);
  const [paxDialogOpen, setPaxDialogOpen] = useState(false);
  const [paxValue, setPaxValue] = useState('');
  const [updatingPax, setUpdatingPax] = useState(false);

  useEffect(() => {
    fetchScorecard();
  }, [entryId]);

  const fetchScorecard = async () => {
    try {
      const res = await axios.get(`${API}/scorecards/${entryId}`);
      setScorecard(res.data);
    } catch (error) {
      console.error('Failed to fetch scorecard:', error);
      toast.error('Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePax = async () => {
    if (!paxValue || parseInt(paxValue) <= 0) {
      toast.error('Please enter a valid PAX value');
      return;
    }

    setUpdatingPax(true);
    try {
      await axios.put(`${API}/entries/${entryId}/pax`, { pax: parseInt(paxValue) });
      toast.success('PAX updated! Scorecard recalculated.');
      setPaxDialogOpen(false);
      fetchScorecard();
    } catch (error) {
      toast.error('Failed to update PAX');
    } finally {
      setUpdatingPax(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  if (!scorecard) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900">Scorecard not found</h2>
            <Button asChild className="mt-4">
              <Link to="/dashboard">Return to Dashboard</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Group metrics by category
  const budgetMetrics = scorecard.metrics.filter(m => 
    ['Budget Attainment', 'Revenue per Labor Hour', 'Transactions per Labor Hour'].includes(m.name)
  );
  const conversionMetrics = scorecard.metrics.filter(m => 
    ['Seen → Sale Conversion', 'Capture → Seen Flow'].includes(m.name)
  );
  const revenueMetrics = scorecard.metrics.filter(m => 
    ['Average Transaction Value'].includes(m.name)
  );
  const captureMetrics = scorecard.metrics.filter(m => 
    ['Capture Rate', 'Overall Sales %', 'Per Cap Sales'].includes(m.name)
  );
  const productMixMetrics = scorecard.metrics.filter(m => 
    ['Supplemental Sales %', 'Digital Mix %', 'Supplemental $/Labor Hour'].includes(m.name)
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="scorecard-view">
        {/* Back button */}
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/scorecards">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Scorecards
          </Link>
        </Button>

        {/* Header Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge 
                    className={scorecard.status === 'final' 
                      ? 'bg-emerald-500 hover:bg-emerald-600' 
                      : 'bg-amber-500 hover:bg-amber-600'
                    }
                  >
                    {scorecard.status === 'final' ? 'Final Scorecard' : 'Preliminary – PAX Pending'}
                  </Badge>
                  {scorecard.ranking && (
                    <Badge className="bg-white/20 hover:bg-white/30">
                      <Trophy className="w-3 h-3 mr-1" />
                      Rank #{scorecard.ranking.rank} of {scorecard.ranking.total}
                    </Badge>
                  )}
                </div>
                <h1 className="text-2xl font-bold font-['Outfit']">{scorecard.location_name}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-white/80 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(scorecard.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {scorecard.manager_name}
                  </span>
                </div>
              </div>
              
              {scorecard.status === 'preliminary' && (
                <Dialog open={paxDialogOpen} onOpenChange={setPaxDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-white text-violet-700 hover:bg-white/90" data-testid="update-pax-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Update PAX
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Update PAX Data</DialogTitle>
                      <DialogDescription>
                        Enter the PAX (total visitors) to finalize the scorecard and unlock capture metrics.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <Input
                        type="number"
                        placeholder="Enter PAX value"
                        value={paxValue}
                        onChange={(e) => setPaxValue(e.target.value)}
                        className="h-12 font-mono"
                        data-testid="pax-input-dialog"
                      />
                      <Button 
                        onClick={handleUpdatePax} 
                        className="w-full bg-violet-600 hover:bg-violet-700"
                        disabled={updatingPax}
                        data-testid="confirm-pax-btn"
                      >
                        {updatingPax ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Update & Recalculate
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Overall Score */}
          <Card className="lg:row-span-2" data-testid="overall-score-card">
            <CardHeader>
              <CardTitle className="text-lg">Overall Score</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center pb-8">
              <ScoreDisplay score={scorecard.overall_score} size="large" />
              
              {scorecard.ranking && (
                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-500">Performance Percentile</p>
                  <p className="text-2xl font-bold text-violet-600 font-mono">
                    Top {Math.round(100 - scorecard.ranking.percentile)}%
                  </p>
                </div>
              )}

              <div className="w-full mt-6 pt-6 border-t">
                <h4 className="font-medium text-slate-700 mb-3">Raw Data Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Budget</span>
                    <span className="font-mono">{formatCurrency(scorecard.raw_data.daily_sales_budget)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Actual Sales</span>
                    <span className="font-mono">{formatCurrency(scorecard.raw_data.actual_total_sales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Labor Hours</span>
                    <span className="font-mono">{scorecard.raw_data.labor_hours_worked}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">PAX</span>
                    <span className={`font-mono ${!scorecard.raw_data.pax ? 'text-amber-600' : ''}`}>
                      {scorecard.raw_data.pax || 'Pending'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Captured</span>
                    <span className="font-mono">{scorecard.raw_data.people_captured}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Seen</span>
                    <span className="font-mono">{scorecard.raw_data.seen}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Transactions</span>
                    <span className="font-mono">{scorecard.raw_data.transactions}</span>
                  </div>
                </div>
              </div>

              {/* Product Mix Summary */}
              <div className="w-full mt-4 pt-4 border-t">
                <h4 className="font-medium text-slate-700 mb-3">Product Mix</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Core Photo</span>
                    <span className="font-mono">{formatCurrency(scorecard.raw_data.core_photo_sales || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Supplemental</span>
                    <span className="font-mono">{formatCurrency(scorecard.raw_data.supplemental_sales || 0)}</span>
                  </div>
                  {scorecard.raw_data.hard_product_sales != null && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 pl-2">↳ Hard Products</span>
                      <span className="font-mono text-slate-500">{formatCurrency(scorecard.raw_data.hard_product_sales)}</span>
                    </div>
                  )}
                  {scorecard.raw_data.digital_product_sales != null && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 pl-2">↳ Digital Products</span>
                      <span className="font-mono text-slate-500">{formatCurrency(scorecard.raw_data.digital_product_sales)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts */}
          <Card className="lg:col-span-2" data-testid="alerts-card">
            <CardHeader>
              <CardTitle className="text-lg">Diagnostic Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <AlertBadge alerts={scorecard.alerts} />
            </CardContent>
          </Card>

          {/* Metrics Grid */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Budget & Labor */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                  Budget & Labor Efficiency (30%)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {budgetMetrics.map(metric => (
                    <MetricCard key={metric.name} metric={metric} />
                  ))}
                </div>
              </div>

              {/* Conversion */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-fuchsia-500"></span>
                  Conversion & Engagement (25%)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {conversionMetrics.map(metric => (
                    <MetricCard key={metric.name} metric={metric} />
                  ))}
                </div>
              </div>

              {/* Revenue Quality */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Revenue Quality (20%)
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {revenueMetrics.map(metric => (
                    <MetricCard key={metric.name} metric={metric} />
                  ))}
                </div>
              </div>

              {/* Product Mix & Upsell */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Product Mix & Upsell (15%)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {productMixMetrics.map(metric => (
                    <MetricCard key={metric.name} metric={metric} />
                  ))}
                </div>
              </div>

              {/* Capture Effectiveness */}
              {captureMetrics.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    Capture Effectiveness (10%)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {captureMetrics.map(metric => (
                      <MetricCard key={metric.name} metric={metric} />
                    ))}
                  </div>
                </div>
              )}

              {scorecard.status === 'preliminary' && captureMetrics.length === 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Capture metrics will be available once PAX data is entered.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
